/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2024-12-12 15:38:57                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-01-27 10:32:27                               *
 *****************************************************************************/

/* SUMMARY
  * FormValidator: form validator class
    * isString: check if value is not null and string type
    * isNotNull: check if value is not null
    * isDate: check if value is date and valid
    * isEmail: check value is valid mail
    * isArrayNotEmpty: check is value is an array and not empty
    * isFieldValid: check if field in data object is valid
    * isFormValid: check if form object is valid
    * isValidCompInfo: check if compinfo is valid
    * isValidSelectCompInfo: check if select compinfo is valid
  * Singleton things
*/

/**
* FieldNames: display name per field, or a function of the data returning the reason it is refused
*/
export type FieldNames = Record<string, string | ((data: any) => string)>;

/**
* FormValidator: form validator class
* usefull function and verification process
*/
export class FormValidator {
  /**
  * isString: check if value is not null and string type
  * @param data - data to check
  * @return - valid or not 
  */
  isString = (data: unknown): boolean => this.isNotNull(data) && typeof data == "string" && (data as string).trim().length > 0;
  /***/

  /**
  * isNumber: check if value is not null and number type
  * @param data - data to check
  * @return - valid or not 
  */
  isNumber = (data: unknown): boolean => this.isNotNull(data) && typeof data === "number";
  /***/

  /**
  * isNotNull: check if value is not null
  * @param data - data to check
  * @return - valid or not 
  */
  isNotNull = (data: unknown): boolean => data !== null && data !== undefined;
  /***/

  /**
  * isDate: check if value is date and valid
  * @param value - value to test
  * @return - valid or not
  */
  isDate = (value: string) => {
    return value.match(/^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}.[0-9]{3}Z/gm);
  };
  /***/

  /**
  * isEmail: check value is valid mail
  * @param value - value to test
  * @return - valid or not 
  */
  isEmail = (value: string) => value.match(/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/gm);
  /***/

  /**
  * isPhone: check if value is valid phone number
  * @param value - value to test
  * @return - valid or not 
  */
  isPhone = (value: string) => value.match(/\(?([0-9]{3})\)?([ .-]?)([0-9]{3})\2([0-9]{4})/gm);
  /***/

  /**
  * isArrayNotEmpty: check is value is an array and not empty
  * @param value - value to check
  * @return - valid or not 
  */
  isArrayNotEmpty = (value: unknown) => Array.isArray(value) && value.length > 0;
  /***/

  /**
  * isFieldValid: check if field in data object is valid
  * use form checker descriptor to check validity
  * @param data - data object to check
  * @param  field - field name to check in data
  * @param checker - validator descriptor
  * @return - valid or not
  *
  * L'objet complet est passé en second argument : une règle peut dépendre d'un autre champ,
  * comme les compInfos dont les contraintes ne s'appliquent qu'à partir d'un certain statut.
  * Les règles qui n'en ont pas besoin l'ignorent simplement.
  */
  isFieldValid = (data: any, field: string, checker: any): boolean => {
    if (checker[field]) { // mandatory
      return checker[field](data[field], data);
    } else return true;
  };
  /***/

  /**
  * isFormValid: check if form object is valid
  * @param data - data to check
  * @param checker - validator descriptor
  * @retunr - valid or not
  */
  isFormValid = (data: any, checker: any) => {
    try {
      const keys = Object.keys(checker);

      for (let i = 0; i < keys.length; i++) {
        if (!checker[keys[i]](data[keys[i]], data)) {
          console.error(`key: ${keys[i]} is invalid...`);
          return false;
        }
      }

      return true;
    } catch (err) {
      console.error(err || "unepexted error...");
      return false;
    }
  };
  /***/

  /**
  * isValidCompInfo: check if compinfo is valid
  * @param ci - compinfo descriptor
  * @param value - compinfo value
  * @return - valid or not
  */
  isValidCompInfo = (ci: EasyTech.CompInfo, value: unknown): boolean => {
    /*
     * La valeur transite en Object côté back et remonte donc tantôt en chaîne saisie par le
     * formulaire, tantôt dans son type d'origine. On la ramène au texte avant de la juger.
     */
    const text = (value === null || value === undefined) ? "" : String(value).trim();
    const optional = ci.constraints !== "REQUIRED";

    // Un champ facultatif vide est valide. Dès qu'il est saisi, le format compte quand même.
    if (optional && text === "") return true;

    try {
      switch (ci.type) {
      case "TEXT":
      case "TEXTAREA":
      case "PASSWORD":
        return text !== "";
      case "NUMBER":
        // isNotNull laissait passer la chaîne vide, et Number.isNaN ne convertit pas son argument :
        // la règle d'origine acceptait un champ obligatoire jamais saisi. La virgule est un
        // séparateur décimal au même titre que le point.
        return text !== "" && /^-?\d+(?:[.,]\d+)?$/.test(text);
      case "DATE":
      case "DATETIME":
        return !!this.isDate(text);
      case "TIME":
        return /^\d{2}:\d{2}$/.test(text);
      case "EMAIL":
        return !!this.isEmail(text);
      case "PHONE":
        return !!this.isPhone(text);
      case "BOOLEAN":
        // Tant que l'interrupteur n'a pas été touché la valeur est vide : aucun choix n'a été fait
        return text === "true" || text === "false";
      default:
        /*
         * Un type que le formulaire ne sait pas rendre n'a pas de champ à remplir. L'exiger
         * bloquerait la soumission sans rien montrer à l'écran, alors on l'accepte — même règle
         * que compInfoValidatorFor : on ne valide que ce qu'on affiche.
         */
        console.warn(`compInfo ${ci.id}: type ${ci.type} inconnu, contrainte ignorée`);
        return true;
      }
    } catch (err) {
      return false;
    }
  };
  /***/

  /**
  * isValidSelectCompInfo: check if select compinfo is valid
  * @param ci - compinfo descriptor
  * @param value - compinfo value
  * @return - valid or not
  */
  isValidSelectCompInfo = (ci: EasyTech.CompInfo, values: EasyTech.CompInfoChoice[]) => {
    if (ci.constraints === "REQUIRED") {
      return values.length > 0 ? true : false;
    } else {
      return true;
    }
  };
  /***/

  /**
  * getInvalidFields: Get list of invalid fields for better error message
  * @param data - data to validate
  * @param validator - validator object
  * @return - array of invalid field names
  */
  getInvalidFields = (data: any, validator: any): string[] => {
    const invalidFields: string[] = [];
    const keys = Object.keys(validator);

    for (let i = 0; i < keys.length; i++) {
      if (!validator[keys[i]](data[keys[i]], data)) {
        invalidFields.push(keys[i]);
      }
    }

    return invalidFields;
  };

  /**
   * getFormErrorMessage: Generate a user-friendly error message for form validation
   * @param data - data to validate
   * @param validator - validator object
   * @param fieldNames - mapping of field names to display names. A field whose rules are worth
   *                     spelling out can map to a function of the data returning the reason itself,
   *                     so the message says what is wrong and not only where
   * @param t - translation function
   * @return - formatted error message
   */
  getFormErrorMessage = (data: any, validator: any, fieldNames: FieldNames, t: any): string => {
    const invalidFields = this.getInvalidFields(data, validator);

    if (invalidFields.length === 0) {
      return t("common:formError");
    }

    const invalidFieldNames = invalidFields.map(field => {
      const name = fieldNames[field];
      return (typeof name === "function" ? name(data) : name) || field;
    });
    return `${t("common:formError")}: ${invalidFieldNames.join(", ")}`;
  };

  /**
   * validateEntityData: Validate entity data using appropriate strategy
   * @param entityName - name of the entity type
   * @param data - data to validate
   * @param t - translation function
   * @return - array of validation error messages
   *
   * Utilise le pattern Strategy avec des validateurs spécialisés par entité
   * Note: Les composants utilisent maintenant directement les validateurs spécialisés
   * Cette méthode est gardée pour compatibilité et extensibilité future
   */
  validateEntityData = (entityName: string, data: any, t: any): string[] => {
    switch (entityName) {
    case "customer":
      // Fallback si le validateur spécialisé n'est pas utilisé directement
      return this.validateCustomerDataFallback(data, t);

      // Pour les futures entités, ajouter ici :
      // case "technician":
      //   return this.validateTechnicianData(data, t);

    default:
      return [];
    }
  };

  /**
   * validateCustomerDataFallback: Validation de secours pour les customers
   * Utilisé si le CustomerValidator spécialisé n'est pas disponible
   */
  private validateCustomerDataFallback = (customerData: any, t: any): string[] => {
    const validationErrors: string[] = [];

    // Logique de validation simplifiée en cas de fallback
    if (!customerData?.name?.trim()) validationErrors.push(t("common:name"));
    if (!customerData?.activitySector) validationErrors.push(t("common:activitySector"));
    if (!customerData?.nip?.trim()) validationErrors.push(t("common:nip"));
    if (!customerData?.address) validationErrors.push(t("common:address"));
    if (!customerData?.languages?.length) validationErrors.push(t("common:languages"));
    if (!customerData?.contactInfo?.email?.trim()) validationErrors.push(t("common:contactInfo") + " - " + t("common:email"));
    if (!customerData?.contactInfo?.phone?.trim()) validationErrors.push(t("common:contactInfo") + " - " + t("common:phone"));

    return validationErrors;
  };
}
/***/

/* Singleton things */
const validatorSingleton = () => {
  return new FormValidator();
};

declare global {
  var validator: undefined | ReturnType<typeof validatorSingleton>;
}

export const validator = globalThis.validator ?? validatorSingleton();
/***/
